import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/Modal'
import { BaseView, BaseText } from '@/components/base'
import { useTheme } from '@/hooks/useTheme'
import { useGetCountriesCatalog } from '@/domain/countries/useCases'
import { normalizeText } from '@/utils/transactionUtils'
import { CountryDTO } from '@/domain/countries/countriesTypes'

interface CountrySelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onCountrySelect: (country: CountryDTO) => void
  selectedCountry: CountryDTO
}

export const CountrySelectionModal: React.FC<CountrySelectionModalProps> = ({
  isOpen,
  onClose,
  onCountrySelect,
  selectedCountry,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [searchValue, setSearchValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  const { data, isLoading: isLoadingCountries } = useGetCountriesCatalog()

  const filteredCountries = useMemo(() => {
    const normalizedSearch = normalizeText(searchValue)

    return data?.countries?.filter(
      ({ name, short_name, code }) =>
        normalizeText(name).includes(normalizedSearch) ||
        normalizeText(short_name).includes(normalizedSearch) ||
        normalizeText(String(code)).includes(normalizedSearch),
    )
  }, [searchValue, data])

  const handleCountrySelect = (country: CountryDTO) => {
    onCountrySelect(country)
    onClose()
    setSearchValue('')
  }

  const handleSearchClear = () => {
    setSearchValue('')
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('translation:signup.chooseCountry')}
      showHandle={true}
      style={{
        height: '75%',
      }}
    >
      <BaseView gap={16}>
        {/* Custom Search Input - RN Style */}
        <BaseView
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            height: '48px',
            paddingLeft: '12px',
            paddingRight: '12px',
            backgroundColor: theme.colors.background.textInput,
            borderRadius: '16px',
            border: isFocused
              ? `1.5px solid ${theme.colors.border.inputTyping}`
              : 'none',
          }}
        >
          {/* Search Icon */}
          <svg
            width="21"
            height="21"
            viewBox="0 0 24 24"
            fill="none"
            style={{
              color: searchValue
                ? theme.colors.icon.activeInputIcon
                : theme.colors.icon.disabled,
            }}
          >
            <path
              d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {/* Input */}
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={t('form:placeholders.search')}
            style={{
              flex: 1,
              height: '100%',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: '16px',
              lineHeight: '20px',
              color: theme.colors.label.textTextInput,
              fontFamily:
                'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontWeight: '400',
            }}
            className="search-input-modal"
          />

          {/* Clear Button */}
          {searchValue && (
            <button
              onClick={handleSearchClear}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '12px',
                backgroundColor: theme.colors.icon.default,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                style={{ color: theme.colors.background.textInput }}
              >
                <path
                  d="M6 18L18 6M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </BaseView>

        {/* Countries List */}
        <BaseView
          style={{
            // maxHeight: '50vh',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <BaseView gap={8}>
            {filteredCountries?.length === 0 ? (
              <BaseView center py={40}>
                <BaseText
                  fontSize={16}
                  fontFamily="regular"
                  color="label.description"
                  textAlign="center"
                >
                  {searchValue
                    ? `${searchValue} ${t('translation:notFound')}`
                    : t('translation:noCountries')}
                </BaseText>
              </BaseView>
            ) : (
              filteredCountries?.map((country) => (
                <button
                  key={country.short_name}
                  onClick={() => handleCountrySelect(country)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px',
                    backgroundColor:
                      selectedCountry.short_name === country.short_name
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '12px',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCountry.short_name !== country.short_name) {
                      e.currentTarget.style.backgroundColor =
                        'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCountry.short_name !== country.short_name) {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }
                  }}
                >
                  <img
                    src={country.img_url}
                    alt={country.name}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '16px',
                      objectFit: 'cover',
                    }}
                  />
                  <BaseView flex={1}>
                    <BaseText
                      fontSize={16}
                      fontFamily="medium"
                      color="label.default"
                      textAlign="left"
                    >
                      {country.name}
                    </BaseText>
                    <BaseText
                      fontSize={14}
                      fontFamily="regular"
                      color="label.description"
                      textAlign="left"
                    >
                      +{country.code}
                    </BaseText>
                  </BaseView>
                </button>
              ))
            )}
          </BaseView>
        </BaseView>
      </BaseView>
    </Modal>
  )
}
